import React, {useState} from 'react';
import {
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Typography,
    Button,
    IconButton,
    Divider,
    Box,
    Stack,
    Tooltip,
    Chip, Grid, LinearProgress
} from '@mui/material';
import {LotItem, LotWithItemList} from "../../../../../types/lot";
import {useTranslation} from "react-i18next";
import {Company, CompanyFile} from "../../../../../types/company";
import {companyService} from "../../../../../api/services/company";
import {publicService} from "../../../../../api/services/public";
import {AxiosError} from "axios";
import {useSnack} from "../../../../../utils/useSnack";

interface LotCardProps {
    id: number;
    lot: LotWithItemList;
    className?: string;
    maxItems?: number;
    company: Company;
    auctionId: number
}

const LotCard: React.FC<LotCardProps> = ({
                                            id,
                                             lot,
                                             className,
                                             maxItems = 5,
    company,
    auctionId
                                         }) => {
    const [expanded, setExpanded] = useState(false);

    // Отображаемые элементы в зависимости от режима expanded
    const displayedItems: LotItem[] = expanded ? lot.lotItems : lot.lotItems.slice(0, maxItems);
    const hasMoreItems = lot.lotItems.length > maxItems;
    const {t} = useTranslation()
    const {snack} = useSnack();
    const [downloadingId, setDownloadingId] = useState<number | null>(null);

    const downloadFile = async (file: CompanyFile) => {

        try {
            setDownloadingId(file.fileId);
            const fileTokenResponse = await publicService.getLotFileToken(auctionId, lot.id, file.fileId);
            if (fileTokenResponse.status === "success" && fileTokenResponse.data) {
                publicService.downloadFile(fileTokenResponse.data.id, file.name);
            } else {
                snack(t("snack.error.file_download"), "error")
            }
        } catch (error) {
            const err = error as AxiosError;
            snack(t("snack.error.default"), "error")
        } finally {
            setDownloadingId(null)
        }
    }
    return (
        <Card
            className={className}
            sx={{
                width: '100%',
                boxShadow: 2,
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.3s',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                }
            }}
        >
            <CardHeader
                title={
                    <Typography variant="h6" noWrap sx={{fontWeight: 'bold'}}>
                        {lot.companyName}
                    </Typography>
                }
            />

            <Divider/>

            <CardContent sx={{pt: 2, pb: 1}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1.5}}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Элементы ({lot.lotItems.length})
                    </Typography>
                    <Typography variant="subtitle2">
                        {new Date(lot.auctionDate).toLocaleDateString()}
                    </Typography>
                </Box>

                {displayedItems.length > 0 ? (
                    <Stack spacing={1.5}>
                        {displayedItems.map((item: LotItem) => (
                            <Box
                                key={item.id}
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    p: 1.5,
                                    borderRadius: 1,
                                    bgcolor: 'background.default',
                                    '&:hover': {bgcolor: 'action.hover'}
                                }}
                            >
                                <Box sx={{overflow: 'hidden', flex: 1, pr: 1}}>
                                    <Typography variant="body2" sx={{fontWeight: 500}}>
                                        {item.productName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
                                        {item.productCode} - {item.productBrief}
                                    </Typography>
                                    <Box sx={{display: 'flex', justifyContent: 'space-between', mt: 0.5}}>
                                        <Typography variant="caption">
                                            {item.amount} {item.unitName}
                                        </Typography>
                                        <Typography variant="caption" sx={{fontWeight: 500}}>
                                            {item.gross.toLocaleString()} ₸
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{py: 2}}>
                        Нет элементов
                    </Typography>
                )}

                {hasMoreItems && !expanded && (
                    <Box sx={{mt: 1.5, textAlign: 'center'}}>
                        <Button
                            size="small"
                            onClick={() => setExpanded(true)}
                        >
                            Показать все ({lot.itemsCount})
                        </Button>
                    </Box>
                )}

                {expanded && hasMoreItems && (
                    <Box sx={{mt: 1.5, textAlign: 'center'}}>
                        <Button
                            size="small"
                            onClick={() => setExpanded(false)}
                        >
                            Свернуть
                        </Button>
                    </Box>
                )}

                <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1.5}}>
                    <Typography variant="subtitle2" color="text.secondary">
                        Файлы ({lot?.attachments?.length || 0})
                    </Typography>
                </Box>
                <Grid item container columnSpacing={2} rowSpacing={1}>
                    {lot?.attachments?.map(file => {
                        return (
                            <Grid item key={file.id}>
                                <Typography fontSize={12} textAlign={"center"} mt={1} mb={.5}>
                                    {t(`file_category.${file.category}`)}
                                </Typography>

                                <Chip key={file.id}
                                      label={file.title || file.name}
                                      onClick={() => downloadFile(file)}
                                      disabled={downloadingId === file.id}
                                />

                                {downloadingId === file.id &&
                                    <LinearProgress color="primary"/>
                                }
                            </Grid>
                        )
                    })}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default LotCard;